/**
 * 统一内容块渲染服务
 *
 * 职责：
 * 1. 统一管理所有内容块的渲染逻辑
 * 2. 支持不同渲染模式：编辑器、预览、邮件
 * 3. 复用项目中的时区工具函数
 * 4. 避免代码重复，确保渲染一致性
 *
 * 国际化策略：
 * - 使用 dateFormatterUtils 中的纯函数进行日期格式化
 * - 所有日期格式化函数显式传入 locale，不依赖全局状态
 * - 默认使用英文 locale（航空业国际标准）
 * - HGH 等中国机场使用中文 locale
 */

import { formatFlightTime } from "../../../utils/timezoneUtils";
import { getEnglishTerm } from "../constants/contentBlockConstants";
import { calculateBlockHours, formatDuration } from "../../../utils/timeUtils";
import { TIMEZONE_OPTIONS } from "../../../contexts/TimeZoneContext";
import { formatDateEN, formatTimeUTC as formatTimeUTCUtil, formatMonthYearEN, getDayOfWeekUTC as getDayOfWeekUTCUtil, AVIATION_DATE_FORMATS } from "../../../utils/dateFormatterUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getEffectiveData } from "../utils/dataScopeResolver";

dayjs.extend(utc);

/**
 * 基础内容渲染器 - 抽象类
 */
class BaseContentRenderer {
    /**
     * UTC时区感知的日期格式化
     * 使用 dateFormatterUtils 的纯函数，默认英文 locale
     */
    formatDateUTC(dateString, format = "YYYY-MM-DD") {
        if (!dateString) return "";
        // 使用新的纯函数工具，显式指定英文 locale
        return formatDateEN(dateString, format);
    }

    /**
     * UTC date formatting with forced English locale
     * 使用 dateFormatterUtils 保证英文输出，不受全局状态影响
     */
    formatDateUTC_EN(dateString, format = "YYYY-MM-DD") {
        if (!dateString) return "";
        // 直接使用保证英文输出的工具函数
        return formatDateEN(dateString, format);
    }

    /**
     * UTC时区感知的时间格式化
     * 时间格式不受 locale 影响，使用纯函数保持一致性
     */
    formatTimeUTC(timeString, format = "HH:mm") {
        if (!timeString) return "";
        return formatTimeUTCUtil(timeString, format);
    }

    /**
     * 获取星期几（UTC时区感知）
     * 使用 dateFormatterUtils 的纯函数
     */
    getDayOfWeekUTC(dateString) {
        if (!dateString) return "";
        return getDayOfWeekUTCUtil(dateString);
    }

    /**
     * 格式化日期为DD-MMM格式（UTC时区感知）
     * 使用 dateFormatterUtils 保证输出英文格式如 "04-Dec"
     */
    formatDateForTable(dateString) {
        if (!dateString) return "";
        // 使用新的纯函数，保证输出英文格式
        return formatDateEN(dateString, AVIATION_DATE_FORMATS.DD_MMM);
    }

    /**
     * 格式化本地时间（基于机场时区）
     * 使用项目标准的formatFlightTime函数和全局机场数据
     */
    formatLocalTime(timeString, airportCode, format = "HH:mm", options = {}) {
        if (!timeString) return "";
        try {
            const airportsMap = this.getAirportsMap(options);

            // 使用项目标准的formatFlightTime函数
            const result = formatFlightTime(timeString, airportCode, TIMEZONE_OPTIONS.LOCAL, airportsMap, null, format);
            return result.time || "";
        } catch (error) {
            console.error("本地时间格式化错误:", error, { timeString, airportCode, format });
            return "";
        }
    }

    /**
     * 获取全局缓存的机场数据映射
     */
    getAirportsMap(options = {}) {
        if (options.airportsMap && typeof options.airportsMap === "object" && Object.keys(options.airportsMap).length > 0) {
            return options.airportsMap;
        }
        if (options.airports && typeof options.airports === "object" && !Array.isArray(options.airports) && Object.keys(options.airports).length > 0) {
            return options.airports;
        }
        if (window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
            const airportsObj = {};
            window.cachedAirports.forEach((airport) => {
                airportsObj[airport.iata_code] = airport;
            });
            return airportsObj;
        }
        return {};
    }
}

/**
 * 计划信息渲染器
 */
export class ScheduleInfoRenderer extends BaseContentRenderer {
    renderForEditor(scheduleData) {
        if (!scheduleData) {
            return {
                type: "div",
                props: {
                    style: { padding: "12px", textAlign: "center", color: "#999" },
                    children: getEnglishTerm("无计划信息"),
                },
            };
        }

        return {
            type: "div",
            props: {
                style: { padding: "12px" },
                children: [
                    { type: "div", props: { style: { marginBottom: "8px" }, children: [{ type: "strong", props: { children: `${getEnglishTerm("计划名称")}：` } }, scheduleData.name || getEnglishTerm("未命名")] } },
                    { type: "div", props: { style: { marginBottom: "8px" }, children: [{ type: "strong", props: { children: `${getEnglishTerm("执行周期")}：` } }, `${this.formatDateUTC(scheduleData.start_date)} ${getEnglishTerm("至")} ${this.formatDateUTC(scheduleData.end_date)}`] } },
                ],
            },
        };
    }

    renderForPreview(scheduleData) {
        return this.renderHTML(scheduleData, false);
    }

    renderForEmail(scheduleData) {
        return this.renderHTML(scheduleData, true);
    }

    renderHTML(scheduleData, isEmail) {
        if (!scheduleData) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("无计划信息")}</div>`;
        }

        const tableStyle = isEmail ? "width: 100%; font-size: 12px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 12px; border-collapse: collapse;";

        return `
            <table style="${tableStyle}">
                <tr>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8; font-weight: bold; background-color: #f5f5f5;">${getEnglishTerm("计划名称")}</td>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8;">${scheduleData.name || getEnglishTerm("未命名")}</td>
                </tr>
                ${
                    scheduleData.start_date
                        ? `
                <tr>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8; font-weight: bold; background-color: #f5f5f5;">${getEnglishTerm("开始日期")}</td>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8;">${this.formatDateUTC(scheduleData.start_date)}</td>
                </tr>
                `
                        : ""
                }
                ${
                    scheduleData.end_date
                        ? `
                <tr>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8; font-weight: bold; background-color: #f5f5f5;">${getEnglishTerm("结束日期")}</td>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8;">${this.formatDateUTC(scheduleData.end_date)}</td>
                </tr>
                `
                        : ""
                }
            </table>
        `;
    }

    renderInlineText(scheduleData) {
        return scheduleData?.name || getEnglishTerm("未命名计划");
    }
}

/**
 * 多计划信息渲染器
 */
export class MultiScheduleInfoRenderer extends BaseContentRenderer {
    renderForEditor(multiScheduleData) {
        if (!multiScheduleData) {
            return {
                type: "div",
                props: {
                    style: { padding: "12px", textAlign: "center", color: "#999" },
                    children: getEnglishTerm("无多计划信息"),
                },
            };
        }

        const { schedules = [], aircraftText = "", dateRangeText = "" } = multiScheduleData;

        return {
            type: "div",
            props: {
                style: { padding: "12px" },
                children: [
                    { type: "div", props: { style: { marginBottom: "8px" }, children: [{ type: "strong", props: { children: `${getEnglishTerm("飞机")}：` } }, aircraftText || getEnglishTerm("未知")] } },
                    { type: "div", props: { style: { marginBottom: "8px" }, children: [{ type: "strong", props: { children: `${getEnglishTerm("执行周期")}：` } }, dateRangeText || getEnglishTerm("未知")] } },
                    { type: "div", props: { style: { marginBottom: "8px" }, children: [{ type: "strong", props: { children: `${getEnglishTerm("计划数量")}：` } }, schedules.length.toString()] } },
                ],
            },
        };
    }

    renderForPreview(multiScheduleData) {
        return this.renderHTML(multiScheduleData, false);
    }

    renderForEmail(multiScheduleData) {
        return this.renderHTML(multiScheduleData, true);
    }

    renderHTML(multiScheduleData, isEmail) {
        if (!multiScheduleData) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("无多计划信息")}</div>`;
        }

        const { schedules = [], aircraftText = "", dateRangeText = "" } = multiScheduleData;
        const tableStyle = isEmail ? "width: 100%; font-size: 12px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 12px; border-collapse: collapse;";

        return `
            <table style="${tableStyle}">
                <tr>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8; font-weight: bold; background-color: #f5f5f5;">${getEnglishTerm("飞机")}</td>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8;">${aircraftText || getEnglishTerm("未知")}</td>
                </tr>
                <tr>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8; font-weight: bold; background-color: #f5f5f5;">${getEnglishTerm("执行周期")}</td>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8;">${dateRangeText || getEnglishTerm("未知")}</td>
                </tr>
                <tr>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8; font-weight: bold; background-color: #f5f5f5;">${getEnglishTerm("计划数量")}</td>
                    <td style="padding: 4px 8px; border: 1px solid #e8e8e8;">${schedules.length}</td>
                </tr>
            </table>
        `;
    }

    renderInlineText(multiScheduleData) {
        if (!multiScheduleData) {
            return getEnglishTerm("未知多计划");
        }

        const { aircraftText = "", dateRangeText = "" } = multiScheduleData;
        return `${aircraftText} on ${dateRangeText}`;
    }
}

/**
 * 航班列表渲染器
 */
export class FlightListRenderer extends BaseContentRenderer {
    renderForEditor(flightData) {
        // 编辑器模式下直接返回HTML字符串，让ContentBlockNodeView使用dangerouslySetInnerHTML渲染
        return this.renderHTML(flightData, false);
    }

    renderForPreview(flightData) {
        return this.renderHTML(flightData, true);
    }

    renderForEmail(flightData) {
        return this.renderHTML(flightData, true);
    }

    renderHTML(flightData, isEmail) {
        if (!Array.isArray(flightData) || flightData.length === 0) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无航班数据")}</div>`;
        }

        const tableStyle = isEmail ? "width: 100%; min-width: 600px; max-width: 900px; font-size: 11px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 11px; border-collapse: collapse;";

        // Sort flights by STD to ensure correct GT calculation
        const sortedFlights = [...flightData].sort((a, b) => {
            const dateA = a.std ? new Date(a.std).getTime() : 0;
            const dateB = b.std ? new Date(b.std).getTime() : 0;
            return dateA - dateB;
        });

        const tableRows = sortedFlights // Remove slicing to show all flights
            .map((flight) => {
                const rowStyle = "padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; vertical-align: middle; white-space: nowrap;";

                // Calculate Block Hours (BH)
                const blockTimeMinutes = calculateBlockHours(flight.std, flight.sta);
                const blockHours = formatDuration(blockTimeMinutes, "HH:mm");

                // ========== GT 字段兼容逻辑 ==========
                // 【问题根因】2025-09-05 提交 06b44bf0 将 GT 从动态计算改为读取字段，
                //            但只考虑了事务处理（FlightOperation）场景，遗漏了计划航班（ScheduleFlight）场景。
                // 【字段差异】
                //   - FlightOperation（执行航班）：使用 actual_ground_time_minutes
                //   - ScheduleFlight（计划航班）：使用 ground_time_minutes
                // 【修复方案】同时检查两个字段，优先使用 actual_ground_time_minutes，
                //            若不存在则回退到 ground_time_minutes。
                // ================================================
                let groundTime = "-";
                const gtMinutes = flight.actual_ground_time_minutes ?? flight.ground_time_minutes;
                if (gtMinutes !== null && gtMinutes !== undefined) {
                    groundTime = formatDuration(gtMinutes, "h_m");
                }

                return `
                <tr style="background-color: #fff;">
                    <td style="${rowStyle}">${this.formatDateForTable(flight.std || flight.flight_date)}</td>
                    <td style="${rowStyle}">${flight.aircraft_reg || "-"}</td>
                    <td style="${rowStyle}">${this.getDayOfWeekUTC(flight.std || flight.flight_date)}</td>
                    <td style="${rowStyle}">${flight.flight_number || ""}</td>
                    <td style="${rowStyle}">${flight.departure_airport || ""}</td>
                    <td style="${rowStyle}">${flight.arrival_airport || ""}</td>
                    <td style="${rowStyle}">${this.formatTimeUTC(flight.std)}</td>
                    <td style="${rowStyle}">${this.formatTimeUTC(flight.sta)}</td>
                    <td style="${rowStyle}; width: 50px;">${flight.stc_type || "-"}</td>
                    <td style="${rowStyle}">${blockHours}</td>
                    <td style="${rowStyle}">${groundTime}</td>
                </tr>
            `;
            })
            .join("");

        return `
                <table style="${tableStyle}">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th colspan="11" style="padding: 8px 6px; border: 1px solid #d9d9d9; font-size: 16px; font-weight: bold; text-align: center; color: #333;">AAE SCHEDULE</th>
                        </tr>
                        <tr style="background-color: #f5f5f5;">
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Date</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">A/C REG</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Day</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Flight</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">DEP</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">ARR</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">STD</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">STA</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center; width: 30px;">STC</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">BH</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">GT</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
        `;
    }

    renderInlineText(flightData) {
        // 目标格式：9H-FLM on 12MAY-29MAY
        if (!Array.isArray(flightData) || flightData.length === 0) {
            return getEnglishTerm("暂无航班数据");
        }

        const firstFlight = flightData[0];
        const aircraftReg = firstFlight.aircraft_reg || "";
        const startDate = firstFlight.std ? this.formatDateUTC_EN(firstFlight.std, "DDMMM") : "";
        const endDate = flightData[flightData.length - 1].std ? this.formatDateUTC_EN(flightData[flightData.length - 1].std, "DDMMM") : "";

        return `${aircraftReg} on ${startDate}-${endDate}`;
    }
}

/**
 * 对比航班列表渲染器
 */
export class ComparisonFlightListRenderer extends BaseContentRenderer {
    renderForEditor(comparisonData) {
        // 编辑器模式下直接返回HTML字符串，让ContentBlockNodeView使用dangerouslySetInnerHTML渲染
        return this.renderHTML(comparisonData, false);
    }

    renderForPreview(comparisonData) {
        return this.renderHTML(comparisonData, false);
    }

    renderForEmail(comparisonData) {
        return this.renderHTML(comparisonData, true);
    }

    renderHTML(comparisonData, isEmail) {
        if (!comparisonData || !comparisonData.flights) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无对比数据")}</div>`;
        }

        const { flights = [], summary = {} } = comparisonData;

        if (flights.length === 0) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无航班数据")}</div>`;
        }

        // 状态样式映射
        const getStatusStyle = (status) => {
            switch (status) {
                case "ADDED":
                    return "background-color: #f6ffed; color: #52c41a;";
                case "CHANGED":
                    return "background-color: #fff7e6; color: #fa8c16;";
                case "REMOVED":
                    return "background-color: #fff1f0; color: #ff4d4f; text-decoration: line-through; opacity: 0.7;";
                default:
                    return "background-color: #fafafa; color: #666;";
            }
        };

        const getStatusText = (status) => {
            switch (status) {
                case "ADDED":
                    return getEnglishTerm("新增");
                case "CHANGED":
                    return getEnglishTerm("变更");
                case "REMOVED":
                    return getEnglishTerm("删除");
                default:
                    return "";
            }
        };

        // 生成表格行
        const tableRows = flights
            .slice(0, 112)
            .map((flight) => {
                const statusStyle = getStatusStyle(flight._comparisonStatus);
                const statusText = getStatusText(flight._comparisonStatus);

                return `
                    <tr style="${statusStyle}">
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px;">${this.formatDateForTable(flight.flight_date)}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px;">${flight.aircraft_reg || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${this.getDayOfWeekUTC(flight.flight_date)}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; font-weight: bold;">${flight.flight_number || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${flight.departure_airport || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${flight.arrival_airport || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${this.formatTimeUTC(flight.std)}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${this.formatTimeUTC(flight.sta)}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; width: 30px;">${flight.stc_type || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${flight.block_hours || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${flight.ground_time || ""}</td>
                        <td style="padding: 2px 4px; border: 1px solid #e8e8e8; font-size: 10px; text-align: center;">${statusText}</td>
                    </tr>
                `;
            })
            .join("");

        // 汇总信息
        const summaryHtml =
            summary && Object.keys(summary).length > 0
                ? `
            <div style="margin-bottom: 12px; padding: 8px; background-color: #f0f8ff; border-radius: 4px; font-size: 12px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${getEnglishTerm("变更汇总")}：</div>
                <div>
                    ${getEnglishTerm("总航班")}：${summary.totalFlights} ${getEnglishTerm("个")} | 
                    <span style="color: #52c41a;">${getEnglishTerm("新增")}：${summary.addedCount}</span> | 
                    <span style="color: #fa8c16;">${getEnglishTerm("变更")}：${summary.changedCount}</span> | 
                    <span style="color: #ff4d4f;">${getEnglishTerm("删除")}：${summary.removedCount}</span>
                    ${summary.unchangedCount > 0 ? ` | <span style="color: #666;">${getEnglishTerm("无变化")}：${summary.unchangedCount}</span>` : ""}
                </div>
            </div>
        `
                : "";

        const tableStyle = isEmail ? "width: 100%; font-size: 11px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 11px; border-collapse: collapse;";

        return `
            <div style="padding: 12px;">
                ${summaryHtml}
                <table style="${tableStyle}">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">Date</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">A/C REG</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">Day</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">Flight</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">DEP</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">ARR</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">STD</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">STA</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold; width: 30px;">STC</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">BH</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">GT</th>
                            <th style="padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; font-size: 11px; font-weight: bold;">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
                ${flights.length > 112 ? `<div style="text-align: center; padding: 8px; color: #999; font-size: 11px;">${getEnglishTerm("显示前")} 112 ${getEnglishTerm("条记录")}, ${getEnglishTerm("共")} ${flights.length} ${getEnglishTerm("条记录")}</div>` : ""}
            </div>
        `;
    }

    renderInlineText(comparisonData, businessContext = {}) {
        // 生成类似 "9H-FLM SCHEDULE [12MAY-29MAY, 12 Flights]" 的格式
        if (comparisonData && (comparisonData.summary || comparisonData.flights)) {
            const totalFlights = comparisonData.summary?.totalFlights || comparisonData.flights?.length || 0;

            let title = "";
            let aircraftReg = "";

            // 获取飞机注册号
            if (comparisonData.flights && comparisonData.flights.length > 0) {
                const flightWithReg = comparisonData.flights.find((f) => f.aircraft_reg);
                aircraftReg = flightWithReg?.aircraft_reg || "";
            }

            // 获取日期范围（UTC时区感知）
            let dateRange = "";
            const scheduleInfo = businessContext.schedule;
            if (scheduleInfo && scheduleInfo.start_date && scheduleInfo.end_date) {
                const startDate = this.formatDateUTC_EN(scheduleInfo.start_date, "DDMMM").toUpperCase();
                const endDate = this.formatDateUTC_EN(scheduleInfo.end_date, "DDMMM").toUpperCase();
                dateRange = `${startDate}-${endDate}`;
            }

            // 组装标题
            if (aircraftReg) {
                title = `${aircraftReg} SCHEDULE`;
            } else {
                title = "SCHEDULE";
            }

            if (dateRange && totalFlights > 0) {
                title += ` [${dateRange}, ${totalFlights} Flights]`;
            } else if (totalFlights > 0) {
                title += ` [${totalFlights} Flights]`;
            }

            return title;
        }
        return "FLIGHT SCHEDULE";
    }
}

/**
 * 航班及前序航班渲染器
 * 只负责渲染，不负责数据查询
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️  数据契约要求 (Data Contract Requirements)                                ║
 * ╠══════════════════════════════════════════════════════════════════════════════╣
 * ║  此渲染器期望接收的数据结构：                                                  ║
 * ║  {                                                                           ║
 * ║    currentFlight: {                                                          ║
 * ║      flight_number, aircraft_reg, std, sta, departure_airport,               ║
 * ║      predecessor: { flight_number, sta, ... }  // ⚠️ 进港航班信息必需         ║
 * ║    },                                                                        ║
 * ║    allFlights: [{ ...同上结构 }, ...]                                         ║
 * ║  }                                                                           ║
 * ║                                                                              ║
 * ║  关键字段说明：                                                               ║
 * ║  - departure_flight_info → 使用 currentFlight 的数据                         ║
 * ║  - arrival_flight_info   → 使用 currentFlight.predecessor 的数据             ║
 * ║                                                                              ║
 * ║  数据来源：                                                                   ║
 * ║  - UnifiedTransactionEmailDrawer.preprocessFlightData()                      ║
 * ║  - 必须使用 extendedFlightData（由 loadExtendedFlightData 加载）              ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see UnifiedTransactionEmailDrawer.preprocessFlightData - 数据生产者
 * @see UnifiedTransactionEmailDrawer.loadExtendedFlightData - predecessor 数据来源
 */

const CATERING_TIME_OFFSET_MAP = Object.freeze({
    NLU: 120,
    ANC: 60,
    ICN: 60,
});
const DEFAULT_CATERING_TIME_OFFSET_MINUTES = 90;
const ICN_KST_OFFSET_HOURS = 9;

export class FlightWithPredecessorRenderer extends BaseContentRenderer {
    /**
     * 检查是否需要中文区域设置格式
     *
     * 业务规则：HGH（杭州萧山机场）的航食邮件需要使用中文格式
     *
     * 影响范围：
     * - 行内显示：日期格式 "M月D日" vs "DDMMM"，飞机号用 "/" 分隔
     * - 表格显示：表头使用中文（出港航班号/航线、计划时间 等）
     *
     * 扩展方式：如需支持更多中国机场，添加到 chineseAirports 数组即可
     *
     * @param {object} flight - 航班对象，必须包含 departure_airport 属性
     * @returns {boolean} - 如果需要中文区域设置则返回 true
     */
    isChineseLocaleRequired(flight) {
        if (!flight || !flight.departure_airport) {
            return false;
        }
        // ⚠️ 业务规则：中国机场航食邮件使用中文格式
        // HGH: 杭州萧山国际机场, CGO: 郑州新郑国际机场
        const chineseAirports = ["HGH", "CGO"];
        return chineseAirports.includes(flight.departure_airport);
    }

    /**
     * 统一的渲染方法，所有模式都使用这个
     */
    renderHTML(flightData, isEmail = false, options = {}) {
        if (!flightData) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无航班数据")}</div>`;
        }

        // 直接使用传入的数据，不做任何处理
        const { currentFlight, allFlights } = flightData;

        if (!currentFlight) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无航班数据")}</div>`;
        }

        // 判断是否为HGH机场，使用中文表头
        const isChinese = this.isChineseLocaleRequired(currentFlight);

        if (isChinese) {
            // 中文模式：使用原有的前序航班表格格式
            const tableStyle = isEmail ? "width: 100%; max-width: 600px; font-size: 12px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 12px; border-collapse: collapse;";

            const headers = {
                departure: "出港航班号/航线",
                scheduledTime: "计划/预计时间",
                arrival: "进港航班号",
                expectedTime: "预达时间",
                cateringTime: "配餐时间",
            };

            return `
                <table style="${tableStyle}">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th style="padding: 8px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">${headers.departure}</th>
                            <th style="padding: 8px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">${headers.scheduledTime}</th>
                            <th style="padding: 8px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">${headers.arrival}</th>
                            <th style="padding: 8px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">${headers.expectedTime}</th>
                            <th style="padding: 8px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">${headers.cateringTime}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this.renderTableRows(allFlights, options)}
                    </tbody>
                </table>
            `;
        } else {
            // 英文模式：使用与 FlightListRenderer 相同的表格格式
            const tableStyle = isEmail ? "width: 100%; min-width: 600px; max-width: 900px; font-size: 11px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 11px; border-collapse: collapse;";

            // 计算主要月份用于表头显示
            const monthCounts = {};
            let maxCount = 0;
            let dominantMonth = null;

            if (allFlights && Array.isArray(allFlights)) {
                allFlights.forEach((flight) => {
                    const flightDate = flight.etd || flight.std || flight.flight_date;
                    if (flightDate) {
                        const date = new Date(flightDate);
                        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                        const monthName = formatMonthYearEN(flightDate, true);

                        monthCounts[monthKey] = monthCounts[monthKey] || { count: 0, name: monthName };
                        monthCounts[monthKey].count++;

                        if (monthCounts[monthKey].count > maxCount) {
                            maxCount = monthCounts[monthKey].count;
                            dominantMonth = monthCounts[monthKey].name;
                        }
                    }
                });
            }

            const scheduleTitle = dominantMonth ? `Flight Schedule ${dominantMonth}` : "Flight Schedule";

            return `
                <table style="${tableStyle}">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th colspan="11" style="padding: 8px 6px; border: 1px solid #d9d9d9; font-size: 16px; font-weight: bold; text-align: center; color: #333;">${scheduleTitle}</th>
                        </tr>
                        <tr style="background-color: #f5f5f5;">
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Date</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">A/C REG</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Day</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Flight</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">DEP</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">ARR</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">STD/ETD</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">STA/ETA</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center; width: 30px;">STC</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">BH</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">GT</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this.renderTableRowsForEnglish(allFlights, options)}
                    </tbody>
                </table>
            `;
        }
    }

    formatTimeWithKST(timeString, airportCode) {
        if (!timeString) return "";
        const utcFormatted = this.formatTimeUTC(timeString);
        if (airportCode === "ICN") {
            const kstTime = dayjs.utc(timeString).add(ICN_KST_OFFSET_HOURS, "hour");
            return `${utcFormatted} (${kstTime.format("HH:mm")} KST)`;
        }
        return utcFormatted;
    }

    formatCateringTimeCell(flight, options = {}) {
        const depTime = flight.etd || flight.std;
        if (!depTime) {
            return "-";
        }
        const offsetMinutes = CATERING_TIME_OFFSET_MAP[flight.departure_airport] ?? DEFAULT_CATERING_TIME_OFFSET_MINUTES;
        const utcCateringTime = dayjs.utc(depTime).subtract(offsetMinutes, "minute");

        if (flight.departure_airport === "ICN") {
            const kstCateringTime = utcCateringTime.add(ICN_KST_OFFSET_HOURS, "hour");
            const crossDay = kstCateringTime.format("YYYY-MM-DD") !== utcCateringTime.format("YYYY-MM-DD");
            const kstFormatted = crossDay ? kstCateringTime.format("MM-DD HH:mm") : kstCateringTime.format("HH:mm");
            return `${utcCateringTime.format("MM-DD HH:mm")} (${kstFormatted} KST)`;
        }

        return `${this.formatLocalTime(utcCateringTime.toISOString(), flight.departure_airport, "MM-DD HH:mm", options)} LT`;
    }

    /**
     * 渲染表格行，显示用户选择的航班和它们的前序航班
     */
    renderTableRows(allFlights, options = {}) {
        if (!allFlights || allFlights.length === 0) {
            return `<tr><td colspan="5" style="padding: 12px; text-align: center; color: #999;">暂无航班数据</td></tr>`;
        }

        return allFlights
            .map((flight) => {
                // 使用航班自带的前序航班数据
                const predecessor = flight.predecessor || null;

                return `
            <tr style="background-color: #fff;">
                <td style="padding: 8px; border: 1px solid #e8e8e8; text-align: center;">
                    ${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport})
                </td>
                <td style="padding: 8px; border: 1px solid #e8e8e8; text-align: center;">
                    ${this.formatLocalTime(flight.etd || flight.std, flight.departure_airport, "MM-DD HH:mm", options)} LT
                </td>
                <td style="padding: 8px; border: 1px solid #e8e8e8; text-align: center;">
                    ${predecessor ? `${predecessor.flight_number} (${predecessor.departure_airport}-${predecessor.arrival_airport})` : "-"}
                </td>
                <td style="padding: 8px; border: 1px solid #e8e8e8; text-align: center;">
                    ${predecessor ? `${this.formatLocalTime(predecessor.eta || predecessor.sta, predecessor.arrival_airport, "MM-DD HH:mm", options)} LT` : "-"}
                </td>
                <td style="padding: 8px; border: 1px solid #e8e8e8; text-align: center;">
                    ${this.formatCateringTimeCell(flight, options)}
                </td>
            </tr>
            `;
            })
            .join("");
    }

    /**
     * 渲染英文模式的表格行（使用 FlightListRenderer 相同的格式）
     */
    renderTableRowsForEnglish(allFlights, options = {}) {
        if (!allFlights || allFlights.length === 0) {
            return `<tr><td colspan="11" style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无航班数据")}</td></tr>`;
        }

        // Sort flights by effective departure time (ETD > STD) to ensure correct GT calculation
        const sortedFlights = [...allFlights].sort((a, b) => {
            const dateA = (a.etd || a.std) ? new Date(a.etd || a.std).getTime() : 0;
            const dateB = (b.etd || b.std) ? new Date(b.etd || b.std).getTime() : 0;
            return dateA - dateB;
        });

        return sortedFlights
            .map((flight) => {
                const rowStyle = "padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; vertical-align: middle; white-space: nowrap;";

                // Calculate Block Hours (BH) - 优先使用预计时间
                const blockTimeMinutes = calculateBlockHours(flight.etd || flight.std, flight.eta || flight.sta);
                const blockHours = formatDuration(blockTimeMinutes, "HH:mm");

                // Use provided Ground Time (GT) data - no fallback logic
                let groundTime = "-";
                if (flight.actual_ground_time_minutes !== null && flight.actual_ground_time_minutes !== undefined) {
                    groundTime = formatDuration(flight.actual_ground_time_minutes, "h_m");
                }

                const effectiveDep = flight.etd || flight.std;
                const effectiveArr = flight.eta || flight.sta;

                return `
                <tr style="background-color: #fff;">
                    <td style="${rowStyle}">${this.formatDateForTable(effectiveDep || flight.flight_date)}</td>
                    <td style="${rowStyle}">${flight.aircraft_reg || "-"}</td>
                    <td style="${rowStyle}">${this.getDayOfWeekUTC(effectiveDep || flight.flight_date)}</td>
                    <td style="${rowStyle}">${flight.flight_number || ""}</td>
                    <td style="${rowStyle}">${flight.departure_airport || ""}</td>
                    <td style="${rowStyle}">${flight.arrival_airport || ""}</td>
                    <td style="${rowStyle}">${this.formatTimeWithKST(effectiveDep, flight.departure_airport)}</td>
                    <td style="${rowStyle}">${this.formatTimeWithKST(effectiveArr, flight.arrival_airport)}</td>
                    <td style="${rowStyle}; width: 50px;">${flight.stc_type || "-"}</td>
                    <td style="${rowStyle}">${blockHours}</td>
                    <td style="${rowStyle}">${groundTime}</td>
                </tr>
            `;
            })
            .join("");
    }

    renderInlineText(flightData, options = {}) {
        if (!flightData) {
            return getEnglishTerm("暂无航班数据");
        }

        const { currentFlight, allFlights } = flightData;
        const { contentBlockId } = options || {};

        if (!currentFlight) {
            return getEnglishTerm("暂无航班数据");
        }

        // 如果有多个航班，使用多航班显示逻辑
        if (allFlights && allFlights.length > 1) {
            return this.renderMultipleFlightsInline(allFlights, contentBlockId, options);
        }

        // 单航班显示逻辑
        return this.renderSingleFlightInline(currentFlight, contentBlockId, options);
    }

    /**
     * 渲染单个航班的行内文本
     *
     * 根据 contentBlockId 决定显示内容：
     * - departure_flight_info：显示当前航班（出港航班）
     * - arrival_flight_info：显示前序航班（进港航班）← ⚠️ 需要 flight.predecessor
     *
     * 特殊业务规则：
     * - HGH（杭州）机场使用中文格式：日期格式为 "M月D日"，航班用 "/" 分隔
     * - 其他机场使用英文格式：日期格式为 "DDMMM"
     *
     * @param {Object} flight - 航班对象（必须包含 predecessor 属性才能正确渲染 arrival_flight_info）
     * @param {string} contentBlockId - 内容块ID，决定显示出港还是进港信息
     *
     * ⚠️ 如果 arrival_flight_info 显示 "-"，请检查：
     *    1. flight.predecessor 是否存在
     *    2. 数据是否来自 extendedFlightData（而非 sortedFlights）
     *    3. preprocessFlightData 是否使用了正确的数据源
     *
     * @see loadExtendedFlightData - predecessor 数据的来源
     * @see isChineseLocaleRequired - HGH 机场中文显示判断
     */
    renderSingleFlightInline(flight, contentBlockId, options = {}) {
        // ⚠️ 关键：中文判断始终基于主航班（flight）的出发机场
        // 这确保了 HGH/CGO 航食邮件中所有内容（包括进港航班）都使用中文格式
        const isChinese = this.isChineseLocaleRequired(flight);

        // ========== 进港航班信息（前序航班）==========
        // arrival_flight_info: 只显示日期（不带时间）
        if (contentBlockId === "arrival_flight_info") {
            const predecessor = flight.predecessor;
            if (!predecessor) {
                return "-";
            }
            const aircraft = predecessor.aircraft_reg || "";
            const flightNum = predecessor.flight_number || "";
            const dateStr = this.formatDateForInline(predecessor.eta || predecessor.sta, flight, options);
            if (isChinese) {
                return `${aircraft}/ ${flightNum} ${dateStr}`;
            } else {
                return `${aircraft} ${flightNum} ${dateStr}`;
            }
        }

        // arrival_flight_datetime: 显示日期+时间 LT
        if (contentBlockId === "arrival_flight_datetime") {
            const predecessor = flight.predecessor;
            if (!predecessor) {
                return "-";
            }
            const aircraft = predecessor.aircraft_reg || "";
            const flightNum = predecessor.flight_number || "";
            const route = predecessor.departure_airport && predecessor.arrival_airport
                ? `（${predecessor.departure_airport}-${predecessor.arrival_airport}）`
                : "";
            const datetimeStr = this.formatDateTimeForInline(predecessor.eta || predecessor.sta, flight, options);
            if (isChinese) {
                return `${aircraft}/ ${flightNum}${route} ${datetimeStr} LT`;
            } else {
                return `${aircraft} ${flightNum}${route} ${datetimeStr}`;
            }
        }

        // ========== 出港航班信息（当前航班）==========
        const aircraft = flight.aircraft_reg || "";
        const flightNum = flight.flight_number || "";

        // departure_flight_datetime: 显示日期+时间 LT（ETD > STD）
        if (contentBlockId === "departure_flight_datetime") {
            const datetimeStr = this.formatDateTimeForInline(flight.etd || flight.std, flight, options);
            if (isChinese) {
                return `<strong>${aircraft}/ ${flightNum} ${datetimeStr} LT</strong>`;
            } else {
                return `<strong>${aircraft} ${flightNum} ${datetimeStr}</strong>`;
            }
        }

        // departure_flight_info（默认）: 只显示日期（不带时间）（ETD > STD）
        const dateStr = this.formatDateForInline(flight.etd || flight.std, flight, options);

        if (isChinese) {
            return `<strong>${aircraft}/ ${flightNum} ${dateStr}</strong>`;
        } else {
            return `<strong>${aircraft} ${flightNum} ${dateStr}</strong>`;
        }
    }

    /**
     * 渲染多个航班的行内文本
     * 对于 departure_flight_info：显示所有选中的出港航班
     * 对于 arrival_flight_info：显示所有选中航班的前序航班（进港航班）
     */
    renderMultipleFlightsInline(allFlights, contentBlockId, options = {}) {
        if (!allFlights || allFlights.length === 0) {
            return getEnglishTerm("暂无航班数据");
        }

        const isChinese = this.isChineseLocaleRequired(allFlights[0]);

        // 对于 arrival_flight_info（进港航班信息），显示前序航班列表
        if (contentBlockId === "arrival_flight_info") {
            // 过滤出有前序航班的航班
            const flightsWithPredecessor = allFlights.filter((f) => f.predecessor);
            if (flightsWithPredecessor.length === 0) {
                return isChinese ? "-" : "-";
            }

            // 取第一个前序航班的飞机注册号
            const aircraft = flightsWithPredecessor[0].predecessor.aircraft_reg || "";

            // 生成前序航班信息列表，最多显示3个
            const maxDisplay = 3;
            const flightInfos = flightsWithPredecessor.slice(0, maxDisplay).map((flight) => {
                const predecessor = flight.predecessor;
                const flightNum = predecessor.flight_number || "";
                // ⚠️ 关键修复：传入主航班（flight）而非 predecessor 来判断中文格式
                // 与 renderSingleFlightInline 保持一致，进港时间 ETA > STA
                const dateStr = this.formatDateForInline(predecessor.eta || predecessor.sta, flight, options);
                return `${flightNum} ${dateStr}`;
            });

            // 如果超过3个航班，添加"等X个航班"
            if (flightsWithPredecessor.length > maxDisplay) {
                const remainingCount = flightsWithPredecessor.length - maxDisplay;
                if (isChinese) {
                    flightInfos.push(`等${remainingCount}个航班`);
                } else {
                    flightInfos.push(`+${remainingCount} more flights`);
                }
            }

            const flightList = flightInfos.join(", ");

            if (isChinese) {
                return `${aircraft}/ ${flightList}`;
            } else {
                return `${aircraft} ${flightList}`;
            }
        }

        // 对于 departure_flight_info（出港航班信息），显示当前选中的航班
        // 取第一个航班的飞机注册号
        const aircraft = allFlights[0].aircraft_reg || "";

        // 生成航班号和日期列表，最多显示3个
        const maxDisplay = 3;
        const flightInfos = allFlights.slice(0, maxDisplay).map((flight) => {
            const flightNum = flight.flight_number || "";
            const dateStr = this.formatDateForInline(flight.etd || flight.std, flight, options);
            return `${flightNum} ${dateStr}`;
        });

        // 如果超过3个航班，添加"等X个航班"
        if (allFlights.length > maxDisplay) {
            const remainingCount = allFlights.length - maxDisplay;
            if (isChinese) {
                flightInfos.push(`等${remainingCount}个航班`);
            } else {
                flightInfos.push(`+${remainingCount} more flights`);
            }
        }

        const flightList = flightInfos.join(", ");

        if (isChinese) {
            return `<strong>${aircraft}/ ${flightList}</strong>`;
        } else {
            return `<strong>${aircraft} ${flightList}</strong>`;
        }
    }

    /**
     * 格式化行内显示的日期
     */
    formatDateForInline(dateStr, flight, options = {}) {
        if (!dateStr) return "";

        const isChinese = this.isChineseLocaleRequired(flight);

        if (isChinese) {
            // For HGH/CGO, use local time (UTC+8 for China).
            // The format should be "M月D日".
            return this.formatLocalTime(dateStr, flight.departure_airport, "M月D日", options);
        } else {
            // For other airports, use UTC time.
            // The format should be "DDMMM" (e.g., 05SEP).
            return this.formatDateUTC_EN(dateStr, "DDMMM").toUpperCase();
        }
    }

    /**
     * 格式化行内显示的日期+时间
     *
     * 中文机场（HGH/CGO）：使用本地时间，格式 "M月D日 HH:mm"
     * 其他机场：使用 UTC 时间，格式 "DDMMM HH:mm"
     *
     * @param {string} dateStr - ISO 8601 格式的日期时间字符串
     * @param {object} flight - 航班对象，用于判断是否中文机场
     * @returns {string} - 格式化后的日期时间字符串
     */
    formatDateTimeForInline(dateStr, flight, options = {}) {
        if (!dateStr) return "";

        const isChinese = this.isChineseLocaleRequired(flight);

        if (isChinese) {
            // For HGH/CGO, use local time (UTC+8 for China).
            // The format should be "M月D日 HH:mm".
            return this.formatLocalTime(dateStr, flight.departure_airport, "M月D日 HH:mm", options);
        } else {
            // For other airports, use UTC time.
            // The format should be "DDMMM HH:mm" (e.g., 05SEP 18:00).
            const datePart = this.formatDateUTC_EN(dateStr, "DDMMM").toUpperCase();
            const timePart = this.formatTimeUTC(dateStr);
            return `${datePart} ${timePart}`;
        }
    }
}

/**
 * 执行航班列表渲染器
 */
export class OperationFlightListRenderer extends BaseContentRenderer {
    renderForEditor(flightData) {
        // 编辑器模式下直接返回HTML字符串，让ContentBlockNodeView使用dangerouslySetInnerHTML渲染
        return this.renderHTML(flightData, false);
    }

    renderForPreview(flightData) {
        return this.renderHTML(flightData, true);
    }

    renderForEmail(flightData) {
        return this.renderHTML(flightData, true);
    }

    renderHTML(flightData, isEmail) {
        if (!Array.isArray(flightData) || flightData.length === 0) {
            return `<div style="padding: 12px; text-align: center; color: #999;">${getEnglishTerm("暂无执行航班数据")}</div>`;
        }

        const tableStyle = isEmail ? "width: 100%; min-width: 600px; max-width: 900px; font-size: 11px; border-collapse: collapse; font-family: Arial, sans-serif;" : "width: 100%; font-size: 11px; border-collapse: collapse;";

        // Sort flights by STD to ensure correct display order
        const sortedFlights = [...flightData].sort((a, b) => {
            const dateA = a.std ? new Date(a.std).getTime() : 0;
            const dateB = b.std ? new Date(b.std).getTime() : 0;
            return dateA - dateB;
        });

        const tableRows = sortedFlights
            .map((flight) => {
                const rowStyle = "padding: 4px 6px; border: 1px solid #e8e8e8; text-align: center; vertical-align: middle; white-space: nowrap;";

                // Calculate Block Hours (BH) - 复用现有的工具函数
                const blockTimeMinutes = calculateBlockHours(flight.std, flight.sta);
                const blockHours = formatDuration(blockTimeMinutes, "HH:mm");

                // 格式化地面时间显示
                const formatGroundTime = (groundTimeMinutes) => {
                    if (!groundTimeMinutes || groundTimeMinutes <= 0) {
                        return "-";
                    }
                    const hours = Math.floor(groundTimeMinutes / 60);
                    const minutes = groundTimeMinutes % 60;
                    if (hours > 0) {
                        return `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`;
                    }
                    return `${minutes}m`;
                };

                return `
                <tr style="background-color: #fff;">
                    <td style="${rowStyle}">${this.formatDateForTable(flight.std || flight.flight_date)}</td>
                    <td style="${rowStyle}">${flight.aircraft_reg || "-"}</td>
                    <td style="${rowStyle}">${this.getDayOfWeekUTC(flight.std || flight.flight_date)}</td>
                    <td style="${rowStyle}">${flight.flight_number || ""}</td>
                    <td style="${rowStyle}">${flight.departure_airport || ""}</td>
                    <td style="${rowStyle}">${flight.arrival_airport || ""}</td>
                    <td style="${rowStyle}">${this.formatTimeUTC(flight.std)}</td>
                    <td style="${rowStyle}">${this.formatTimeUTC(flight.sta)}</td>
                    <td style="${rowStyle}; width: 30px;">${flight.stc_type || "-"}</td>
                    <td style="${rowStyle}">${blockHours}</td>
                    <td style="${rowStyle}">${formatGroundTime(flight.actual_ground_time_minutes)}</td>
                </tr>
            `;
            })
            .join("");

        const scheduleTitle = "AAE SCHEDULE";

        return `
                <table style="${tableStyle}">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th colspan="11" style="padding: 8px 6px; border: 1px solid #d9d9d9; font-size: 16px; font-weight: bold; text-align: center; color: #333;">${scheduleTitle}</th>
                        </tr>
                        <tr style="background-color: #f5f5f5;">
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Date</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">A/C REG</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Day</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">Flight</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">DEP</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">ARR</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">STD</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">STA</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center; width: 30px;">STC</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">BH</th>
                            <th style="padding: 8px 6px; border: 1px solid #d9d9d9; font-weight: bold; text-align: center;">GT</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
        `;
    }

    renderInlineText(flightData) {
        // 目标格式：<strong style="background-color: #ffff00; padding: 2px 4px;">9H-WFF flights</strong>
        if (!Array.isArray(flightData) || flightData.length === 0) {
            return getEnglishTerm("暂无执行航班数据");
        }

        // 获取第一个航班的飞机注册号
        const aircraftReg = flightData[0]?.aircraft_reg || "SKED";

        // 统计航班月份分布，找到航班数量最多的月份
        const monthCounts = {};
        let maxCount = 0;
        let dominantMonth = null;

        flightData.forEach((flight) => {
            const flightDate = flight.std || flight.flight_date;
            if (flightDate) {
                const date = new Date(flightDate);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                const monthName = formatMonthYearEN(flightDate, true);

                monthCounts[monthKey] = monthCounts[monthKey] || { count: 0, name: monthName };
                monthCounts[monthKey].count++;

                if (monthCounts[monthKey].count > maxCount) {
                    maxCount = monthCounts[monthKey].count;
                    dominantMonth = monthCounts[monthKey].name;
                }
            }
        });

        // 构建文本：如果有主要月份，则显示月份信息
        const monthText = dominantMonth ? ` ${dominantMonth}` : "";

        // 返回加粗和高亮的飞机注册号 + flights + 月份
        return `<strong style="background-color: #ffff00; padding: 2px 4px;">${aircraftReg} flights${monthText}</strong>`;
    }
}

/**
 * 内容块渲染器 - 策略模式实现
 */
export class ContentBlockRenderer {
    constructor() {
        // 内容渲染器
        this.contentRenderers = {
            scheduleInfo: new ScheduleInfoRenderer(),
            multiScheduleInfo: new MultiScheduleInfoRenderer(),
            scheduleFlightList: new FlightListRenderer(),
            scheduleFlightListWithComparison: new ComparisonFlightListRenderer(),
            operationFlightList: new OperationFlightListRenderer(),
            flightWithPredecessor: new FlightWithPredecessorRenderer(),
        };
    }

    /**
     * 渲染内容块 - 编辑器模式
     */
    renderForEditor(contentBlockType, businessData, options = {}) {
        const contentRenderer = this.contentRenderers[contentBlockType];

        if (!contentRenderer) {
            console.error(`未知的内容块类型: ${contentBlockType}`);
            return this.renderFallback(contentBlockType, "editor");
        }

        return contentRenderer.renderHTML(businessData, false, options);
    }

    /**
     * 渲染内容块 - 预览模式
     */
    renderForPreview(contentBlockType, businessData, options = {}) {
        const contentRenderer = this.contentRenderers[contentBlockType];

        if (!contentRenderer) {
            console.error(`未知的内容块类型: ${contentBlockType}`);
            return this.renderFallback(contentBlockType, "preview");
        }

        return contentRenderer.renderHTML(businessData, false, options);
    }

    /**
     * 渲染内容块 - 邮件模式
     */
    renderForEmail(contentBlockType, businessData, options = {}) {
        const contentRenderer = this.contentRenderers[contentBlockType];

        if (!contentRenderer) {
            console.error(`未知的内容块类型: ${contentBlockType}`);
            return this.renderFallback(contentBlockType, "email");
        }

        return contentRenderer.renderHTML(businessData, true, options);
    }

    /**
     * 渲染行内内容块文本
     */
    renderInlineText(contentBlockType, businessData, options = {}) {
        const contentRenderer = this.contentRenderers[contentBlockType];

        if (!contentRenderer) {
            return `[${contentBlockType}]`;
        }

        const effectiveBusinessData = getEffectiveData(options.contentBlockInstance || options.instance, businessData);
        return contentRenderer.renderInlineText(effectiveBusinessData, options);
    }

    /**
     * 回退渲染（当找不到对应渲染器时）
     */
    renderFallback(contentBlockType, renderMode) {
        const fallbackText = `[${getEnglishTerm("未知内容块类型")}: ${contentBlockType}]`;

        if (renderMode === "editor") {
            return {
                type: "div",
                props: {
                    style: { padding: "12px", textAlign: "center", color: "#999", fontStyle: "italic" },
                    children: fallbackText,
                },
            };
        }

        return `<div style="padding: 12px; text-align: center; color: #999; font-style: italic;">${fallbackText}</div>`;
    }
}

// 创建并导出单例实例
export const contentBlockRenderer = new ContentBlockRenderer();

// 导出默认实例
export default contentBlockRenderer;
